/**
 * Phase 19.10 — Beat Playlists list page.
 *
 * Tabs:
 *   - My Playlists (auth required)
 *   - Featured (admin-curated)
 *   - Trending (auto-ranked by plays + saves)
 *   - Public (newest)
 *
 * Producer/Artist boundary: this page hits /api/beat-playlists exclusively.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const TABS = [
  { key: "my", label: "My Playlists", auth: true },
  { key: "featured", label: "Featured" },
  { key: "trending", label: "Trending" },
  { key: "public", label: "Browse" },
];

const PlaylistCard = ({ pl }) => (
  <Link
    to={`/beat-playlists/${pl.playlist_id}`}
    className="block group"
    data-testid={`beat-playlist-card-${pl.playlist_id}`}
  >
    <Card className="overflow-hidden bg-white/[0.04] border-white/10 hover:border-purple-400/50 transition">
      <div className="aspect-square w-full bg-gradient-to-br from-purple-900/40 to-pink-900/40 relative">
        {pl.cover_image && (
          <img src={optimizeImage(pl.cover_image, 'card')} alt=""
               className="w-full h-full object-cover"
               loading="lazy" />
        )}
        {pl.featured && (
          <span className="absolute top-2 left-2 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-black">
            ⭐ Featured
          </span>
        )}
        <span className="absolute bottom-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full bg-black/70 text-purple-200">
          🎼 {pl.beat_count ?? (pl.beat_ids?.length || 0)} beats
        </span>
      </div>
      <div className="p-3">
        <p className="text-sm font-bold text-white truncate">{pl.name}</p>
        <p className="text-[11px] text-gray-400 truncate">
          by {pl.creator?.artist_name || pl.creator?.name || "—"}
        </p>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
          <span>▶ {pl.plays || 0}</span>
          <span>🔖 {pl.saves || 0}</span>
        </div>
      </div>
    </Card>
  </Link>
);

const BeatPlaylistsPage = () => {
  const { currentUser: user } = useCurrentUser();
  const [tab, setTab] = useState(user ? "my" : "featured");
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const fetchTab = (which) => {
    setLoading(true);
    const urlMap = {
      my: "/beat-playlists/my",
      featured: "/beat-playlists/featured?limit=24",
      trending: "/beat-playlists/trending?limit=24",
      public: "/beat-playlists/public?limit=30",
    };
    api.get(urlMap[which])
      .then((r) => setPlaylists(r.data?.playlists || []))
      .catch((e) => {
        if (e?.response?.status === 401) {
          setPlaylists([]);
          if (which === "my") toast.error("Sign in to see your playlists");
        } else {
          setPlaylists([]);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await api.post("/beat-playlists", { name, is_public: true });
      setPlaylists((ps) => [r.data, ...ps]);
      setNewName(""); setShowCreate(false);
      toast.success(`Created "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create playlist");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="beat-playlists-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">RGM Marketplace</p>
            <h1 className="text-3xl sm:text-4xl font-black text-white">
              🎼 Beat Playlists
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Producer-curated. Featured + Genre + Trending lanes. Kept separate from artist playlists.
            </p>
          </div>
          {user && (
            <Button
              onClick={() => setShowCreate((v) => !v)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              data-testid="bp-create-toggle"
            >
              + New Playlist
            </Button>
          )}
        </div>

        {showCreate && user && (
          <form onSubmit={handleCreate} className="mb-6 flex gap-2 max-w-md"
                data-testid="bp-create-row">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Playlist name…"
              autoFocus
              className="bg-black/40 text-white border-gray-700"
              data-testid="bp-create-name"
            />
            <Button
              type="submit"
              disabled={!newName.trim() || creating}
              className="bg-purple-600 hover:bg-purple-500 text-white"
              data-testid="bp-create-submit"
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </form>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.filter((t) => !t.auth || user).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`bp-tab-${t.key}`}
              className={`px-4 py-1.5 rounded-full text-sm font-bold border whitespace-nowrap transition-colors ${
                tab === t.key
                  ? "bg-purple-600/40 border-purple-400 text-white"
                  : "bg-black/30 border-gray-700 text-gray-300 hover:border-purple-500/60"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400" data-testid="bp-loading">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
            <p className="mt-3 text-sm uppercase tracking-widest">Loading playlists…</p>
          </div>
        ) : playlists.length === 0 ? (
          <Card className="p-10 text-center text-gray-400 bg-gray-900/40 border-gray-800"
                data-testid="bp-empty">
            <div className="text-5xl mb-3">🎼</div>
            <p className="text-lg font-semibold text-white">
              {tab === "my"
                ? "You haven't created a beat playlist yet."
                : "No playlists here yet."}
            </p>
            <p className="text-sm mt-2">
              {tab === "my"
                ? "Hit + New Playlist above to start curating."
                : "Check back soon — producers are curating playlists every day."}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
               data-testid="bp-grid">
            {playlists.map((pl) => <PlaylistCard key={pl.playlist_id} pl={pl} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeatPlaylistsPage;
